import React from 'react';
import {View, Text} from 'react-native';
import {withDatabase} from '@nozbe/watermelondb/react';
import {withObservables} from '@nozbe/watermelondb/react';
import withState from 'src/redux/wrapper';
import {compose} from 'recompose';
import {Q} from '@nozbe/watermelondb';
import {useStyle} from 'src/providers/style';
import {Typography} from 'src/styles';
import {Program, User} from 'src/models';
import TargetName from 'src/modules/session/components/target-name';
import {mergeMap, of} from 'rxjs';
import moment from 'moment';
import PromptView from 'src/modules/session/components/prompt-view';
import ListItemSeparator from 'src/common-components/separator';

const safeParseArray = (value: any): string[] => {
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const RenderFieldComponent = ({
  keyName,
  oldValue,
  newValue,
  program,
}: any) => {
  const styles = useStyle();

  const formatValue = (value: any) =>
    !value && program?.method === 'frequency'
      ? '1'
      : !value
      ? 'None'
      : String(value);
  const formatIntensity = (value: any) => (!value ? 'None' : String(value));

  switch (keyName) {
    case 'intensity':
      return formatIntensity(oldValue) === formatIntensity(newValue) ? (
        <Text style={Typography.P3}>-</Text>
      ) : (
        <Text style={Typography.P3}>
          {formatIntensity(oldValue)} → {formatIntensity(newValue)}
        </Text>
      );
    case 'deleted_at':
      return <Text style={Typography.P3}>{newValue ? 'Yes' : 'No'}</Text>;

    case 'start_timestamp':
      if (!newValue || newValue === 0) {
        return <Text style={Typography.P3}>-</Text>;
      }
      return (
        <Text style={Typography.P3}>
          {moment(oldValue).format('hh:mm:ss A')} →{' '}
          {moment(newValue).format('hh:mm:ss A')}
        </Text>
      );

    case 'end_timestamp':
      if (!oldValue || oldValue === 0 || !newValue || newValue === 0) {
        return <Text style={Typography.P3}>-</Text>;
      }
      return (
        <Text style={Typography.P3}>
          {moment(oldValue).format('hh:mm:ss A')} →{' '}
          {moment(newValue).format('hh:mm:ss A')}
        </Text>
      );

    case 'prompts':
      const oldPrompts = safeParseArray(oldValue);
      const newPrompts = safeParseArray(newValue);

      if (!oldPrompts.length && !newPrompts.length) {
        return (
          <View style={[styles.row, styles.alignCenter, styles.flexWrap]}>
            <Text style={Typography.P3}>-</Text>
          </View>
        );
      }

      return (
        <View style={[styles.row, styles.alignCenter, styles.flexWrap]}>
          {!oldPrompts.length && <Text style={Typography.P3}>None</Text>}
          {oldPrompts.map(id => (
            <PromptView
              key={id}
              promptId={id}
              fieldName="name"
              textStyle="P3"
            />
          ))}
          <Text style={[styles.paddingSMHorizontal]}>→</Text>
          {!newPrompts.length && <Text style={Typography.P3}>None</Text>}
          {newPrompts.map(id => (
            <PromptView
              key={id}
              promptId={id}
              fieldName="name"
              textStyle="P3"
            />
          ))}
        </View>
      );

    default:
      return formatValue(oldValue) === formatValue(newValue) ? (
        <Text style={Typography.P3}>{formatValue(newValue)}</Text>
      ) : (
        <Text style={Typography.P3}>
          {formatValue(oldValue)} → {formatValue(newValue)}
        </Text>
      );
  }
};

export const VISIBLE_FIELDS = [
  'value',
  'intensity',
  'prompts',
  // 'number_of_trials',
  'deleted_at',
  'start_timestamp',
  'end_timestamp',
];

export const friendlyFieldName = (key: string) => {
  const map: Record<string, string> = {
    value: 'Value',
    intensity: 'Intensity',
    prompts: 'ABC',
    // number_of_trials: 'Number of Trials',
    deleted_at: 'Deleted',
    start_timestamp: 'Start Timestamp',
    end_timestamp: 'End Timestamp',
  };
  return map[key] || key;
};

const isMeaningfulChange = (a: any, b: any): boolean => {
  if ((a === null && b === undefined) || (a === undefined && b === null)) {
    return false;
  }
  return JSON.stringify(a) !== JSON.stringify(b);
};

const getDiff = (oldObj: any, newObj: any) => {
  const diff: Record<string, {old: any; new: any}> = {};
  const allKeys = new Set([
    ...Object.keys(oldObj || {}),
    ...Object.keys(newObj || {}),
  ]);

  allKeys.forEach(key => {
    if (!VISIBLE_FIELDS.includes(key)) {
      return;
    }

    const oldVal = oldObj?.[key];
    const newVal = newObj?.[key];

    if (isMeaningfulChange(oldVal, newVal)) {
      diff[key] = {old: oldVal, new: newVal};
    }
  });

  return diff;
};

const AmendedDataTableRow = ({item, editor, program}: any) => {
  const styles = useStyle();

  let oldValue: any, newValue: any, diff: any;
  try {
    oldValue = JSON.parse(item.data.old_value);
    newValue = JSON.parse(item.data.new_value);
    const isDeleted = !!newValue?.deleted_at;
    diff = isDeleted
      ? Object.fromEntries(
          VISIBLE_FIELDS.filter(
            key =>
              oldValue?.[key] !== undefined || newValue?.[key] !== undefined,
          ).map(key => [key, {old: oldValue?.[key], new: newValue?.[key]}]),
        )
      : getDiff(oldValue, newValue);
  } catch (e) {
    console.error('Audit diff parse error', e);
    return null;
  }

  const filteredDiff = Object.entries(diff);

  if (filteredDiff.length === 0 && item?.data?.entity_type !== 'sessions') {
    return null;
  }

  if (
    item?.data?.entity_type === 'sessions' &&
    newValue?.amended_at === oldValue?.amended_at &&
    newValue?.amendment_reason === oldValue?.amendment_reason
  ) {
    return null;
  }

  return item?.data?.entity_type === 'sessions' ? (
    <View style={[styles.paddingSMVertical]}>
      <View style={[styles.row, styles.alignCenter, styles.paddingSMVertical]}>
        <View style={[styles.flex, styles.paddingRight]}>
          <Text style={[Typography.LABEL_MEDIUM]}>
            This session's data was modified by {editor?.firstName}{' '}
            {editor?.lastName} on{' '}
            {moment(newValue?.amended_at).format('MM/DD/YYYY')} at{' '}
            {moment(newValue?.amended_at).format('h:mm A')}
            {'. '}
            Amendment reason given: "{newValue?.amendment_reason}"
          </Text>
        </View>
      </View>
      <ListItemSeparator />
    </View>
  ) : (
    <View style={[styles.paddingSMVertical]}>
      <View style={[styles.row, styles.alignCenter, styles.paddingSMVertical]}>
        <View style={[styles.flex, styles.paddingRight]}>
          <Text style={[Typography.P3]}>
            {editor?.firstName} {editor?.lastName}
          </Text>
        </View>
        <View style={[styles.flex, styles.paddingRight]}>
          <Text style={[Typography.P3]}>
            {moment(item.data.created_at).format('MM/DD/YYYY h:mm A')}
          </Text>
        </View>
        <View style={[styles.flex, styles.paddingRight]}>
          <Text style={[Typography.P3]}>{program?.name}</Text>
        </View>
        <View style={[styles.flex, styles.paddingRight]}>
          {item?.target ? (
            <TargetName targetId={item?.target} textStyle={[Typography.P3]} />
          ) : (
            <Text style={[Typography.P3]}>-</Text>
          )}
        </View>

        {VISIBLE_FIELDS.map(fieldKey => {
          const change = diff[fieldKey];
          return (
            <View key={fieldKey} style={[styles.flex, styles.paddingRight]}>
              {change ? (
                <RenderFieldComponent
                  keyName={fieldKey}
                  oldValue={change.old}
                  newValue={change.new}
                  program={program}
                />
              ) : (
                <Text style={[Typography.P3]}>-</Text>
              )}
            </View>
          );
        })}
      </View>
      <ListItemSeparator />
    </View>
  );
};

export default compose(
  withDatabase,
  withState,
  withObservables(['item'], ({item, database}: any) => ({
    editor: database
      .get(User.table)
      .query(
        Q.where('_partition', item.data.partition),
        Q.where('identifier', item.data.created_by),
      )
      .observe()
      .pipe(mergeMap(x => x)),
    program:
      item?.data?.entity_type !== 'sessions'
        ? database.get(Program.table).findAndObserve(item?.program)
        : of({}),
  })),
)(AmendedDataTableRow);
